import { useState, useEffect } from 'react';
import { FormData, ManualFlightData, Flight, Car, Status, User, AIRCRAFT_PARTS, AircraftPartRequest } from '../types/aircraft';
import dataService from '../services/dataService';
import flightService from '../services/flightService';
import carService from '../services/carService';
import authService from '../services/authService';

export const useRequestForm = () => {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    flightId: 0,
    carId: 0,
    statusId: 0,
    creatorId: 0,
    responsibleId: 0,
    aircraftParts: [], // Изменено на массив запросов частей
    priority: 'MEDIUM'
  });

  const [flights, setFlights] = useState<Flight[]>([]);
  const [cars, setCars] = useState<Car[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Состояния для улучшенного интерфейса
  const [flightInputMode, setFlightInputMode] = useState<'ready' | 'manual'>('ready');
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [manualFlightData, setManualFlightData] = useState<ManualFlightData>({
    flightNumber: '',
    departureAirport: '',
    arrivalAirport: '',
    departureTime: '',
    arrivalTime: ''
  });
  const [selectedAircraftParts, setSelectedAircraftParts] = useState<string[]>([]); // ID выбранных частей
  const [hoveredPart, setHoveredPart] = useState<string>('');
  const [aircraftView, setAircraftView] = useState<'top' | 'front'>('top');

  // Загрузка данных
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [flightsRes, carsRes, statusesRes, usersRes] = await Promise.all([
          flightService.getFlights(),
          carService.getCars(),
          dataService.getStatuses(),
          dataService.getUsers()
        ]);

        setFlights(flightsRes.content || []);
        setCars(carsRes.content || []);
        setStatuses(statusesRes.content || []);
        setUsers(usersRes.content || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Обработчики событий
  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleManualFlightChange = (field: string, value: string) => {
    setManualFlightData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAircraftPartSelect = (partId: string) => {
    console.log('Выбрана часть самолета:', partId);
    const part = AIRCRAFT_PARTS.find(p => p.id === partId);
    if (part) {
      const newSelectedParts = selectedAircraftParts.includes(partId)
        ? selectedAircraftParts.filter(id => id !== partId)
        : [...selectedAircraftParts, partId];
      
      setSelectedAircraftParts(newSelectedParts);
      
      // Обновляем aircraftParts в formData
      const aircraftPartsRequests: AircraftPartRequest[] = newSelectedParts.map(id => {
        const foundPart = AIRCRAFT_PARTS.find(p => p.id === id);
        return {
          aircraftPartType: foundPart?.view.toUpperCase() || 'UNKNOWN',
          aircraftPartName: foundPart?.name || '',
          description: `Выбрана часть: ${foundPart?.name}`
        };
      });
      
      setFormData((prev) => ({
        ...prev,
        aircraftParts: aircraftPartsRequests
      }));
    }
  };

  const handleFlightsUpdate = async () => {
    try {
      const flightsRes = await flightService.getFlights();
      setFlights(flightsRes.content || []);
    } catch (error) {
      console.error('Error updating flights:', error);
    }
  };

  const handlePartHover = (partId: string) => {
    setHoveredPart(partId);
  };

  const handlePartLeave = () => {
    setHoveredPart('');
  };

  const handleFlightSelect = (flightId: number) => {
    handleInputChange('flightId', flightId);
  };

  const handleAircraftPartChange = (partNames: string[]) => {
    // Обновляем выбранные части на схеме
    const partIds = partNames.map(name => {
      const part = AIRCRAFT_PARTS.find(p => p.name === name);
      return part ? part.id : '';
    }).filter(id => id);
    
    setSelectedAircraftParts(partIds);
    
    // Обновляем aircraftParts в formData
    const aircraftPartsRequests: AircraftPartRequest[] = partIds.map(id => {
      const foundPart = AIRCRAFT_PARTS.find(p => p.id === id);
      return {
        aircraftPartType: foundPart?.view.toUpperCase() || 'UNKNOWN',
        aircraftPartName: foundPart?.name || '',
        description: `Выбрана часть: ${foundPart?.name}`
      };
    });
    
    setFormData((prev) => ({
      ...prev,
      aircraftParts: aircraftPartsRequests
    }));
  };

  const handleViewChange = (view: 'top' | 'front') => {
    setAircraftView(view);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const userId = authService.getUserId();
      if (!userId) {
        throw new Error('Не удалось определить пользователя');
      }

      const requestData = {
        ...formData,
        creatorId: userId,
        responsibleId: userId
      };

      await dataService.createRequest(requestData);
      return true;
    } catch (error) {
      console.error('Error creating request:', error);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      flightId: 0,
      carId: 0,
      statusId: 0,
      creatorId: 0,
      responsibleId: 0,
      aircraftParts: [],
      priority: 'MEDIUM'
    });
    setSelectedAircraftParts([]);
    setHoveredPart('');
    setAircraftView('top');
    setManualFlightData({
      flightNumber: '',
      departureAirport: '',
      arrivalAirport: '',
      departureTime: '',
      arrivalTime: ''
    });
  };

  return {
    // Состояние
    formData,
    flights,
    cars,
    statuses,
    users,
    loading,
    submitting,
    flightInputMode,
    selectedFlight,
    manualFlightData,
    selectedAircraftParts,
    hoveredPart,
    aircraftView,
    
    // Обработчики
    handleInputChange,
    handleManualFlightChange,
    handleAircraftPartSelect,
    handleFlightsUpdate,
    handlePartHover,
    handlePartLeave,
    handleFlightSelect,
    handleAircraftPartChange,
    handleViewChange,
    handleSubmit,
    resetForm,
    
    // Утилиты
    setFlightInputMode,
    setSelectedFlight
  };
}; 